import React, { useState, useEffect } from 'react';

const FaqsAdmin = ({ API, adminToken, isDarkMode, showNotification }) => {
  // FAQ states
  const [faqs, setFaqs] = useState([]);
  const [editingFaq, setEditingFaq] = useState(null);
  const [newFaq, setNewFaq] = useState({
    question: '',
    answer: '',
    order: 0,
    isActive: true
  });

  const authHeaders = {
    'Authorization': `Bearer ${adminToken}`,
    'Content-Type': 'application/json'
  };

  // Load FAQs on mount
  useEffect(() => {
    loadFaqs();
  }, []);

  // ===============================
  // FAQ MANAGEMENT FUNCTIONS
  // ===============================

  const loadFaqs = async () => {
    try {
      const response = await fetch(`${API}/admin/faqs`, {
        headers: authHeaders
      });

      if (response.ok) {
        const data = await response.json();
        setFaqs(data);
      }
    } catch (error) {
      console.error('Error loading FAQs:', error);
    }
  };

  const handleCreateFaq = async () => {
    if (!newFaq.question.trim() || !newFaq.answer.trim()) {
      showNotification('Please fill in both question and answer', 'error');
      return;
    }

    try {
      const response = await fetch(`${API}/admin/faqs`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(newFaq)
      });

      if (response.ok) {
        showNotification('FAQ created successfully', 'success');
        setNewFaq({ question: '', answer: '', order: 0, isActive: true });
        loadFaqs();
      } else {
        showNotification('Failed to create FAQ', 'error');
      }
    } catch (error) {
      console.error('Error creating FAQ:', error);
      showNotification('Failed to create FAQ', 'error');
    }
  };

  const handleUpdateFaq = async (faqId, updates) => {
    try {
      const params = new URLSearchParams(updates);
      const response = await fetch(`${API}/admin/faqs/${faqId}?${params}`, {
        method: 'PUT',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('FAQ updated successfully', 'success');
        setEditingFaq(null);
        loadFaqs();
      } else {
        showNotification('Failed to update FAQ', 'error');
      }
    } catch (error) {
      console.error('Error updating FAQ:', error);
      showNotification('Failed to update FAQ', 'error');
    }
  };

  const handleDeleteFaq = async (faqId) => {
    if (!window.confirm('Are you sure you want to delete this FAQ?')) {
      return;
    }

    try {
      const response = await fetch(`${API}/admin/faqs/${faqId}`, {
        method: 'DELETE',
        headers: authHeaders
      });

      if (response.ok) {
        showNotification('FAQ deleted successfully', 'success');
        loadFaqs();
      } else {
        showNotification('Failed to delete FAQ', 'error');
      }
    } catch (error) {
      console.error('Error deleting FAQ:', error);
      showNotification('Failed to delete FAQ', 'error');
    }
  };

  const handleToggleFaqActive = async (faqId, currentStatus) => {
    await handleUpdateFaq(faqId, { isActive: !currentStatus });
  };

  return (
    <div className="space-y-6">
      {/* Add New FAQ */}
      <div className={`border rounded-lg p-6 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-white'}`}>
        <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ➕ Add New FAQ
        </h3>
        <div className="space-y-4">
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Question
            </label>
            <input
              type="text"
              value={newFaq.question}
              onChange={(e) => setNewFaq({...newFaq, question: e.target.value})}
              placeholder="e.g., Can I cancel my subscription anytime?"
              className={`w-full px-3 py-2 rounded-lg border ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
              }`}
            />
          </div>
          <div>
            <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
              Answer
            </label>
            <textarea
              value={newFaq.answer}
              onChange={(e) => setNewFaq({...newFaq, answer: e.target.value})}
              placeholder="Yes! You can cancel your Pro subscription at any time..."
              rows="3"
              className={`w-full px-3 py-2 rounded-lg border ${
                isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
              }`}
            />
          </div>
          <div className="flex gap-4">
            <div>
              <label className={`block text-sm font-medium mb-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                Order
              </label>
              <input
                type="number"
                value={newFaq.order}
                onChange={(e) => setNewFaq({...newFaq, order: parseInt(e.target.value) || 0})}
                className={`w-20 px-3 py-2 rounded-lg border ${
                  isDarkMode ? 'bg-gray-700 border-gray-600 text-white' : 'border-gray-300'
                }`}
              />
            </div>
            <div className="flex items-end">
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={newFaq.isActive}
                  onChange={(e) => setNewFaq({...newFaq, isActive: e.target.checked})}
                  className="rounded"
                />
                <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                  Active
                </span>
              </label>
            </div>
          </div>
          <button
            onClick={handleCreateFaq}
            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 font-medium"
          >
            Create FAQ
          </button>
        </div>
      </div>

      {/* FAQ List */}
      <div className="space-y-3">
        <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
          ❓ All FAQs ({faqs.length})
        </h3>

        {faqs.length === 0 ? (
          <div className={`text-center py-12 ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
            <p>No FAQs yet. Create your first FAQ above.</p>
          </div>
        ) : (
          faqs.map((faq) => (
            <div key={faq.id} className={`border rounded-lg p-4 ${
              isDarkMode ? 'border-gray-700 bg-gray-700' : 'border-gray-200 bg-white'
            } ${!faq.isActive ? 'opacity-60' : ''}`}>
              {editingFaq === faq.id ? (
                // Edit Mode
                <div className="space-y-3">
                  <input
                    type="text"
                    defaultValue={faq.question}
                    onBlur={(e) => handleUpdateFaq(faq.id, { question: e.target.value })}
                    className={`w-full px-3 py-2 rounded-lg border font-semibold ${
                      isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                    }`}
                  />
                  <textarea
                    defaultValue={faq.answer}
                    onBlur={(e) => handleUpdateFaq(faq.id, { answer: e.target.value })}
                    rows="3"
                    className={`w-full px-3 py-2 rounded-lg border ${
                      isDarkMode ? 'bg-gray-600 border-gray-500 text-white' : 'border-gray-300'
                    }`}
                  />
                  <button
                    onClick={() => setEditingFaq(null)}
                    className="text-sm bg-green-600 text-white px-3 py-1 rounded hover:bg-green-700"
                  >
                    Done
                  </button>
                </div>
              ) : (
                // View Mode
                <div>
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <span className={`inline-block px-2 py-1 rounded text-xs font-medium mr-2 ${
                        isDarkMode ? 'bg-blue-900 text-blue-300' : 'bg-blue-100 text-blue-800'
                      }`}>
                        Order: {faq.order}
                      </span>
                      {!faq.isActive && (
                        <span className={`inline-block px-2 py-1 rounded text-xs font-medium ${
                          isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}>
                          Inactive
                        </span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => setEditingFaq(faq.id)}
                        className="text-blue-600 hover:text-blue-700"
                      >
                        ✏️ Edit
                      </button>
                      <button
                        onClick={() => handleToggleFaqActive(faq.id, faq.isActive)}
                        className={faq.isActive ? 'text-orange-600 hover:text-orange-700' : 'text-green-600 hover:text-green-700'}
                      >
                        {faq.isActive ? '👁️ Hide' : '👁️ Show'}
                      </button>
                      <button
                        onClick={() => handleDeleteFaq(faq.id)}
                        className="text-red-600 hover:text-red-700"
                      >
                        🗑️ Delete
                      </button>
                    </div>
                  </div>
                  <h4 className={`font-semibold mb-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                    {faq.question}
                  </h4>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {faq.answer}
                  </p>
                </div>
              )}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default FaqsAdmin;
